import React from 'react';
import { Provider } from 'react-redux';
import Research from './research';
import configureStore from './store/configure';
import { getInitialTab } from './store/local_storage';

import { isPresent } from 'helpers/string_helpers';
import { initialState } from './research_constants';
import { useEdgeCrmStyleOverrides } from 'bundles/crm/lite_crm_overrides/helpers';

import type {
  AssetClassOption,
  BaseOption,
  Country,
  CurrentUser,
  Database,
  Features,
  MarkerClustererStyle,
  Paths,  
} from './types';
import type { DromoImporterProps } from 'components/dromo/types';

import './styles.scss';

interface ResearchIndexProps {
  aggregateView: boolean;
  assetClasses: AssetClassOption[];
  availabilities: BaseOption[];
  availableDatabases: Database[];
  availableRailProviders: BaseOption[];
  buildingClass: BaseOption[];
  canAccessCrm: boolean;
  canDelete: boolean;
  canExport: boolean;
  canFindDuplicates: boolean;
  canGroup: boolean;
  canManageTags: boolean;
  canTransferRecords?: boolean;
  countryList: Country[];
  currentUser: CurrentUser;
  databaseId: number;
  dromoImporterProps: DromoImporterProps;
  features: Features;
  googleMapsApiKey?: string;
  initialPage?: string;
  investmentTypes: BaseOption[];
  leaseSpaceType: BaseOption[];
  leaseType: BaseOption[];
  leaseUnits: BaseOption[];
  lotBorderTypes: BaseOption[];
  lotStabilizations: BaseOption[];
  markerAnchor: number[];
  markerClustererStyles: MarkerClustererStyle[];
  marketTypeOptions: BaseOption[];
  microMarketTypeOptions: BaseOption[];
  paths: Paths;
  servedRailProviders: BaseOption[];
  subMarketTypeOptions: BaseOption[];
  svnMember: boolean;
  tags: string[];
  transferRecordBatch?: Object;
}
export default function ResearchIndex({
  aggregateView,
  assetClasses,
  availabilities,
  availableDatabases,
  availableRailProviders,
  buildingClass,
  canAccessCrm,
  canDelete,
  canExport,
  canFindDuplicates,
  canGroup,
  canManageTags,
  canTransferRecords,
  countryList,
  currentUser,
  databaseId,
  dromoImporterProps,
  features,
  googleMapsApiKey,
  initialPage,
  investmentTypes,
  leaseSpaceType,
  leaseType,
  leaseUnits,
  lotBorderTypes,
  lotStabilizations,
  markerAnchor,
  markerClustererStyles,
  marketTypeOptions,
  microMarketTypeOptions,
  paths,
  servedRailProviders,
  subMarketTypeOptions,
  svnMember,
  tags,
  transferRecordBatch,
}: ResearchIndexProps) {
  const defaultColumns = ['databaseName', 'propertyType', 'size', 'type', 'ownerName', 'ownerEmail'];
  if (features.edgeCrm) {
    defaultColumns.shift();
    useEdgeCrmStyleOverrides();
  }

  const store = configureStore({
    paths,
    properties: {
      bulkTags: [],
      deletingGroups: null,
      editingGroups: null,
      groups: [],
      startBulkTagging: false,
      startBulkDelete: false,
      showCopyRecords: false,
      showMoveRecords: false
    },
    dromoImporterProps,
    duplicates: {
      isLoading: true,
      page: 1,
      properties: [],
      totalProperties: 0,
      viewActive: false
    },
    features,
    query: {
      researchGroupId: null,
      propertyPage: isPresent(initialPage) ? initialPage : 1,
      searchText: '',
      propertySort: '',
      availabilities: [],
      assetClasses: [],
      buildingClasses: [],
      excludeTags: false,
      investmentTypes: [],
      tags: [],
      associationFilters: { ...initialState.associationFilters },
      addressFilter: { ...initialState.addressFilter },
      propertyFilter: { ...initialState.propertyFilter },
      saleFilter: { ...initialState.saleFilter },
      industrialFilter: { ...initialState.industrialFilter },
      multifamilyFilter: { ...initialState.multifamilyFilter }
    },
    savedSearches: {
      loading: true,
      deleteSavedSearch: null
    },
    scope: {
      currentUser: currentUser,
      databaseId: databaseId
    },
    search: {
      initialFetchComplete: false,
      isFetching: true,
      properties: [],
      meta: {
        currentPage: 0,
        pageSize: 5,
        totalCount: 0,
        totalPages: 0,
        totalUngroupedCount: 0,
        mapLocations: [],
        propertyIds: []
      }
    },
    selectList: {
      selectedPropertyIds: []
    },
    session: {
      currentColumns: defaultColumns
    },
    static: {
      aggregateView: aggregateView,
      assetClasses: assetClasses,
      availabilities: availabilities,
      availableDatabases: availableDatabases,
      availableRailProviders: availableRailProviders,
      buildingClasses: buildingClass,
      countryList: countryList,
      investmentTypes: investmentTypes,
      leaseSpaceTypes: leaseSpaceType,
      leaseTypes: leaseType,
      leaseUnits: leaseUnits,
      lotBorderTypes: lotBorderTypes,
      lotStabilizations: lotStabilizations,
      marketTypeOptions: marketTypeOptions,
      microMarketTypeOptions: microMarketTypeOptions,
      servedRailProviders: servedRailProviders,
      subMarketTypeOptions: subMarketTypeOptions,
      tags: tags
    },
    transfers: {
      batch: transferRecordBatch
    },
    ui: {
      activeMapProperty: null,
      alertMessage: '',
      canAccessCrm: canAccessCrm,
      canDelete: canDelete,
      canExport: canExport,
      canFindDuplicates: canFindDuplicates,
      canGroup: canGroup,
      canManageTags: canManageTags,
      canTransferRecords: canTransferRecords,
      currentTab: getInitialTab() || 'maps',
      defaultColumns: defaultColumns,
      error: false,
      filtersVisible: true,
      googleMapsApiKey: googleMapsApiKey || '',
      markerAnchor: markerAnchor,
      markerClustererStyles: markerClustererStyles,
      showMapInfoWindow: false,
      showMapList: true,
      showColumnFilters: false,
      svnMember: svnMember
    }
  });

  return (
    <Provider store={store}>
      <Research />
    </Provider>
  );
}
